import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// Página pública de visualização de proposta — acesso anônimo via /publico/proposta/{hash}.
// Não usa Authenticator nem chamadas com token.
//
// Critério de design (ondas 1+2 do batch comercial):
//   - O conteúdo (doc.conteudo_final) já carrega <style> embutido com layout A4
//     completo (CSS_PROPOSTA_EMBUTIDO no comercial-engine). A página pública
//     aplica o mínimo de wrapper possível para deixar esse CSS mandar — visual
//     idêntico ao preview do modal e ao PDF gerado pelo Puppeteer.
//   - Título da capa usa shrink-to-fit em JS: se passar de 2 linhas, reduz
//     font-size até caber. Trabalha junto com o clamp() do CSS embutido.
export default function PaginaPublicaProposta({ hash }: { hash: string }) {
  const [doc, setDoc] = useState<any>(null);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(true);
  const conteudoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/publico/proposta/${hash}`);
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        // Se o backend respondeu com formato de cliente (drive consolidado), redireciona.
        // O backend reconhece hash de cliente e devolve { cliente, contratos }.
        if (data?.cliente && Array.isArray(data?.contratos)) {
          window.location.href = `/publico/cliente/${hash}`;
          return;
        }
        setDoc(data);
      } catch (e: any) {
        setErro(e.response?.data?.error || e.response?.data?.erro || 'Documento não encontrado ou link inválido');
      } finally { setLoading(false); }
    })();
  }, [hash]);

  // Shrink-to-fit do título da capa: ajusta font-size até caber em 2 linhas.
  // Necessário porque títulos curtos ficam ótimos com clamp() do CSS, mas títulos
  // longos transbordam. Roda após o conteúdo ser inserido no DOM.
  useEffect(() => {
    if (!doc?.conteudo_final || !conteudoRef.current) return;
    // Aguarda fontes/imagens carregarem antes de medir
    const t = setTimeout(() => ajustarFonteCapa(conteudoRef.current!), 150);
    // Re-ajusta em resize da janela (clamp() é em vw, escala junto)
    const onResize = () => ajustarFonteCapa(conteudoRef.current!);
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(t); window.removeEventListener('resize', onResize); };
  }, [doc?.conteudo_final]);

  function imprimir() {
    // [86cd8b7a-fix6] Injeta @page no <head> antes de imprimir.
    // Browsers respeitam @page no head com mais consistência do que
    // dentro de <style> em body (que é onde nosso conteudo_final declara).
    // Isso garante que a capa fullbleed funcione mesmo no Edge.
    const styleId = 'hd-print-page-rules';
    let s = document.getElementById(styleId);
    if (!s) {
      s = document.createElement('style');
      s.id = styleId;
      s.textContent = `
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
        }
      `;
      document.head.appendChild(s);
    }
    window.print();
  }

  if (loading) return <Tela>Carregando...</Tela>;
  if (erro) return <Tela><div style={{ color: '#B91626' }}>⚠ {erro}</div></Tela>;
  if (!doc) return null;

  return (
    <div className="proposta-publica-root" style={{ minHeight: '100vh', background: '#F0F1F5' }}>
      {/* Barra superior — não imprime */}
      <div className="no-print" style={{
        background: '#020D7F', color: '#fff', padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <strong style={{ fontSize: 15 }}>Hydrodata Engenharia</strong>
          <div style={{ fontSize: 11, opacity: 0.85 }}>
            {doc.tipo === 'venda' ? 'Documento de venda' : 'Proposta comercial'} · v{doc.versao}{doc.revisao > 0 ? ` · R${doc.revisao}` : ''}
          </div>
        </div>
        <button onClick={imprimir} style={{
          padding: '7px 14px', fontSize: 12, borderRadius: 5,
          border: 'none', background: '#fff', color: '#020D7F',
          cursor: 'pointer', fontWeight: 600,
        }}>🖨️ Imprimir / PDF</button>
      </div>

      {/* Conteúdo: largura A4 (~794px) sem padding extra — deixa o CSS embutido
          do conteudo_final controlar o layout interno.
          [86cd8b7a-fix2] classe proposta-wrapper-public zera margin no print —
          margin de 20px aqui empurrava a capa pra fora da página A4 quando
          o usuário usava "Imprimir / PDF" do navegador. */}
      <div ref={conteudoRef} className="proposta-wrapper-public" style={{
        maxWidth: 794, margin: '20px auto', background: 'transparent',
      }}>
        <div className="proposta-conteudo"
          dangerouslySetInnerHTML={{ __html: doc.conteudo_final }} />

        {/* Bloco de aceite (não está no conteudo_final) */}
        <div className="no-print" style={{
          background: '#fff', padding: '20px 22mm', boxSizing: 'border-box',
          marginTop: 0, borderTop: '0.5px solid #e0e0e0',
          boxShadow: '0 0 0 1px #ddd',
        }}>
          {doc.aceite_em ? (
            <div style={{
              padding: 14, background: '#0a7f4a15', border: '0.5px solid #0a7f4a',
              borderRadius: 6, color: '#0a7f4a',
            }}>
              <strong>✓ Esta proposta foi aceita digitalmente.</strong>
              <div style={{ fontSize: 12, marginTop: 4, color: '#222' }}>
                Aceitante: <strong>{doc.aceite_nome}</strong><br />
                Data: {new Date(doc.aceite_em).toLocaleString('pt-BR')}
              </div>
            </div>
          ) : (
            <FormAceite hash={hash} onAceito={(d) => setDoc({ ...doc, ...d })} />
          )}
        </div>
      </div>

      {/* Footer — não imprime */}
      <div className="no-print" style={{
        textAlign: 'center', padding: 16, fontSize: 10, color: '#888',
      }}>
        Documento gerado pelo CRM Hydrodata{doc.link_publicado_em
          ? ` · ${new Date(doc.link_publicado_em).toLocaleDateString('pt-BR')}` : ''}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          /* [86cd8b7a-fix4] Reset agressivo de TODOS os wrappers da página
             pública. Antes, qualquer minHeight, padding ou margin nesses
             containers empurrava a capa pra fora da página A4 — e a capa
             "caía" pra próxima folha. Agora todos viram pass-through. */
          .proposta-publica-root {
            background: #fff !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .proposta-wrapper-public {
            margin: 0 !important;
            max-width: none !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .proposta-conteudo, .proposta-render, .proposta-moderna {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Garante que a capa ocupa exatamente uma página, sem overflow */
          .pm-capa, .capa-ploomes {
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
        /* @page declarado dentro do conteudo_final inclui named pages
           (capa fullbleed + interna com margem). Não duplicamos aqui pra não
           sobrescrever inadvertidamente. */
      `}</style>
    </div>
  );
}

// Ajusta font-size do título da capa até caber em até 2 linhas.
// Entra no place em ~50ms após render. Volta a ajustar quando a janela muda
// de tamanho (porque clamp() do CSS escala em vw).
// [86cd8b7a] Cobre os 2 templates (clássico .capa-titulo-grande e
// moderno .pm-capa-titulo). Range: max do clamp → floor 18px.
function ajustarFonteCapa(container: HTMLElement) {
  const seletores = ['.capa-titulo-grande', '.pm-capa-titulo'];
  for (const sel of seletores) {
    const titulos = container.querySelectorAll<HTMLElement>(sel);
    for (const el of Array.from(titulos)) {
      el.style.fontSize = '';
      el.style.maxHeight = '';
      el.style.whiteSpace = '';
      const cs = getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight);
      if (!Number.isFinite(lh) || lh <= 0) continue;
      const limiteAlt = Math.round(lh * 2 + 2);
      if (el.scrollHeight <= limiteAlt) continue;
      let size = parseFloat(cs.fontSize) || 36;
      while (el.scrollHeight > limiteAlt && size > 18) {
        size -= 1;
        el.style.fontSize = size + 'px';
      }
    }
  }
}

function FormAceite({ hash, onAceito }: { hash: string; onAceito: (d: any) => void }) {
  const [nome, setNome] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  async function aceitar() {
    if (!nome.trim()) { setErro('Por favor, preencha seu nome completo'); return; }
    if (!window.confirm(`Confirma o aceite desta proposta em nome de "${nome}"? Esta ação é registrada com data, hora e IP.`)) return;
    setEnviando(true);
    setErro('');
    try {
      const res = await axios.post(`${API_URL}/publico/proposta/${hash}/aceitar`, { nome });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      onAceito({ aceite_em: data.aceite_em, aceite_nome: data.aceite_nome });
    } catch (e: any) {
      setErro(e.response?.data?.error || e.response?.data?.erro || 'Erro ao registrar aceite');
    } finally { setEnviando(false); }
  }

  return (
    <div style={{ padding: 16, background: '#FAFBFF', border: '0.5px solid #CDD4EA', borderRadius: 6 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#020D7F' }}>
        Aceitar proposta digitalmente
      </h3>
      <p style={{ fontSize: 12, color: '#555', marginTop: 0, marginBottom: 12 }}>
        Ao clicar em <strong>Aceitar</strong>, você confirma a leitura e concordância com os termos desta proposta.
        Ficam registrados: nome informado, data/hora, endereço IP e navegador para fins de comprovação.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Seu nome completo"
          value={nome} onChange={e => setNome(e.target.value)}
          style={{
            flex: 1, minWidth: 260, padding: '8px 12px', fontSize: 13,
            borderRadius: 5, border: '0.5px solid #ddd',
          }} />
        <button onClick={aceitar} disabled={enviando}
          style={{
            padding: '8px 22px', fontSize: 13, fontWeight: 600,
            border: 'none', borderRadius: 5, background: '#0a7f4a', color: '#fff',
            cursor: enviando ? 'wait' : 'pointer',
          }}>
          {enviando ? 'Registrando...' : '✓ Aceitar proposta'}
        </button>
      </div>
      {erro && <div style={{ marginTop: 8, color: '#B91626', fontSize: 12 }}>{erro}</div>}
    </div>
  );
}

function Tela({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#F0F1F5',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#fff', padding: 40, borderRadius: 8,
        maxWidth: 480, width: '100%', textAlign: 'center', fontSize: 13,
      }}>
        <h2 style={{ color: '#020D7F', marginTop: 0, fontSize: 18 }}>Hydrodata Engenharia</h2>
        {children}
      </div>
    </div>
  );
}
